import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Paper,
  Typography,
  useMediaQuery,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Tooltip,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  BarChart,
  Bar,
  Legend,
  LabelList,
} from "recharts";
import { useNavigate } from "react-router-dom";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import IconButton from '@mui/material/IconButton';
import { fetchAllServicePayments } from "../api";

// small helper legend we will show UNDER the donut on mobile
const MobileLegend = ({ data, colors }) => {
  return (
    <Box
      sx={{
        mt: 1.5,
        display: "flex",
        flexWrap: "wrap",
        justifyContent: "center",
        rowGap: 1,
        columnGap: 2,
        textAlign: "center",
      }}
    >
      {data.map((item, idx) => (
        <Box
          key={idx}
          sx={{
            display: "flex",
            alignItems: "center",
            fontSize: "0.8rem",
            fontWeight: 600,
            color: "text.primary",
          }}
        >
          <Box
            sx={{
              width: 10,
              height: 10,
              borderRadius: "50%",
              bgcolor: colors[idx % colors.length],
              mr: 1,
            }}
          />
          <Typography
            component="span"
            sx={{ fontSize: "0.8rem", fontWeight: 600, lineHeight: 1.2 }}
          >
            {item.label}{" "}
            <Typography
              component="span"
              sx={{
                fontWeight: 500,
                color: "text.secondary",
                fontSize: "0.8rem",
                ml: 0.5,
              }}
            >
              ({(item.value || 0).toLocaleString("en-IN")})
            </Typography>
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

function PatPayReportGraphs() {
  const isSmallScreen = useMediaQuery("(max-width:800px)");
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [payments, setPayments] = useState([]);
  const [selectedPro, setSelectedPro] = useState("");

  useEffect(() => {
    const fetchPayments = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetchAllServicePayments();
        setPayments(res.data || []);
      } catch (err) {
        setError("Failed to fetch payment details.");
      } finally {
        setLoading(false);
      }
    };
    fetchPayments();
  }, []);

  // Process data for charts
  const processChartData = (data) => {
    // 1. Payment Status Data (Paid vs Unpaid)
    const paidAmount = data.reduce(
      (sum, p) => sum + (p.TASP_IsPaid ? Number(p.TASP_Amount) || 0 : 0),
      0
    );
    const unpaidAmount = data.reduce(
      (sum, p) => sum + (!p.TASP_IsPaid ? Number(p.TASP_Amount) || 0 : 0),
      0
    );
    const paymentStatusData = [
      { id: 0, value: paidAmount, label: "Paid" },
      { id: 1, value: unpaidAmount, label: "Unpaid" },
    ];

    // 2. Payment Type Data (Online vs Offline)
    const onlinePayments = data.filter((p) => p.TASP_Payment_Type === 1).length;
    const offlinePayments = data.filter(
      (p) => p.TASP_Payment_Type === 2
    ).length;
    const paymentTypeData = [
      { id: 0, value: onlinePayments, label: "Online" },
      { id: 1, value: offlinePayments, label: "Offline" },
    ];

    // 3. Revenue by Professional (Pie / aggregates)
    const proRevenueMap = {};
    let totalAssigned = 0;
    data.forEach((p) => {
      const proName = p.proName || null;
      const amount = Number(p.TASP_Amount) || 0;
      if (proName) {
        proRevenueMap[proName] = (proRevenueMap[proName] || 0) + amount;
        totalAssigned += amount;
      }
    });
    const proRevenueData = Object.entries(proRevenueMap).map(
      ([proName, amount]) => ({ proName, amount })
    );

    // 4. Revenue by Service Type (Bar Chart)
    const serviceRevenueMap = {};
    data.forEach((p) => {
      const serviceName =
        p.TASP_Request_Service_Ref?.TA_Service_Name || "Unknown Service";
      serviceRevenueMap[serviceName] =
        (serviceRevenueMap[serviceName] || 0) + (Number(p.TASP_Amount) || 0);
    });
    const serviceRevenueData = Object.entries(serviceRevenueMap).map(
      ([serviceName, amount]) => ({
        serviceName,
        amount,
      })
    );

    // 5. Revenue Over Time (Line Graph - monthly for now)
    const monthlyRevenueMap = {};
    data.forEach((p) => {
      if (p.TASP_Created_At) {
        const date = new Date(p.TASP_Created_At);
        const monthYear = `${date.getFullYear()}-${String(
          date.getMonth() + 1
        ).padStart(2, "0")}`; // YYYY-MM
        monthlyRevenueMap[monthYear] =
          (monthlyRevenueMap[monthYear] || 0) + (Number(p.TASP_Amount) || 0);
      }
    });
    const revenueOverTimeData = Object.entries(monthlyRevenueMap)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([monthYear, amount]) => ({
        date: monthYear,
        amount,
      }));

    return {
      paymentStatusData,
      paymentTypeData,
      proRevenueData,
      totalAssigned,
      serviceRevenueData,
      revenueOverTimeData,
    };
  };

  const chartData = payments.length > 0 ? processChartData(payments) : null;

  // consistent color palette across pies / legends
  const COLORS = [
    "#4caf50",
    "#ff9800",
    "#2196f3",
    "#9c27b0",
    "#00bcd4",
    "#f44336",
  ];

  // dynamic sizing for service bar chart
  const serviceCount = chartData?.serviceRevenueData?.length || 0;
  const maxServiceNameLen =
    chartData?.serviceRevenueData?.reduce(
      (m, d) => Math.max(m, (d.serviceName || "").length),
      0
    ) || 0;
  const yAxisWidth = Math.min(300, Math.max(120, maxServiceNameLen * 8));
  // height grows with number of services; cap for sanity
  const serviceChartHeight = Math.min(
    1000,
    Math.max(260, serviceCount * 40 + 120)
  );

  return (
    <Box
      sx={{ background: "#f4f6f8", minHeight: "100vh", p: { xs: 1.5, sm: 2 } }}
    >
      {/* Header Card */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, sm: 3 },
          mb: { xs: 2, sm: 3 },
          display: "flex",
          borderRadius: 3,
          justifyContent: "space-between",
          gap: 2,
          background: "white",
          boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
          flexDirection: { xs: "row", md: "row" },
          alignItems: { xs: "center", md: "center" },
        }}
      >
        <Typography
          variant={isSmallScreen ? "h6" : "h4"}
          sx={{
            fontWeight: 700,
            flex: 1,
            textTransform: "capitalize",
            color: "black",
            textAlign: { xs: "left", md: "left" },
            lineHeight: 1.2,
          }}
        >
          Revenue Report Graphs
        </Typography>

        {isSmallScreen?(
          <IconButton
            onClick={() => navigate("/ppayment-report")}
            color="primary"
          >
            <ArrowBackIcon />
          </IconButton>
        ):(
          <Button
            onClick={() => navigate("/ppayment-report")}
            variant="contained"
            sx={{
              color: "#fff",
              fontWeight: 600,
              textTransform: "none",
              px: { xs: 2, sm: 3 },
              py: { xs: 1, sm: 1 },
              borderRadius: 3,
              alignSelf: { xs: "stretch", md: "center" },
            }}
          >
            Back To Report
          </Button>
        )}
      </Paper>

      {loading ? (
        <Box sx={{ p: 4, textAlign: "center" }}>
          <Typography sx={{ color: "background.text" }}>
            Loading chart data...
          </Typography>
        </Box>
      ) : error ? (
        <Box sx={{ p: 4, textAlign: "center", color: "red" }}>
          <Typography>{error}</Typography>
        </Box>
      ) : !chartData ? (
        <Box sx={{ p: 4, textAlign: "center" }}>
          <Typography sx={{ color: "background.text" }}>
            No data available for charts.
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={isSmallScreen ? 2 : 3}>
          {/* ====================== Payment Status ====================== */}
          <Grid item xs={12} md={4}>
            <Paper
              elevation={2}
              sx={{
                p: { xs: 2, sm: 2.5 },
                borderRadius: 3,
                background: "white",
                boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
                textAlign: { xs: "center", md: "left" },
                display: "flex",
                flexDirection: "column",
                alignItems: { xs: "center", md: "stretch" },
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  mb: 2,
                  color: "black",
                  fontSize: { xs: "1rem", md: "1.1rem" },
                }}
              >
                Payment Status
              </Typography>

              <Box
                sx={{
                  width: "100%",
                  height: { xs: 200, sm: 230, md: 250 },
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={chartData.paymentStatusData}
                      dataKey="value"
                      nameKey="label"
                      cx={isSmallScreen ? "50%" : "40%"}
                      cy="50%"
                      outerRadius={isSmallScreen ? 70 : 90}
                      innerRadius={isSmallScreen ? 40 : 50}
                      // floating labels removed on all screen sizes
                      labelLine={false}
                      label={false}
                      isAnimationActive={false}
                    >
                      {chartData.paymentStatusData.map((entry, index) => (
                        <Cell
                          key={`ps-${index}`}
                          fill={COLORS[index % COLORS.length]}
                        />
                      ))}
                    </Pie>

                    {/* center total for mobile */}
                    {isSmallScreen && (
                      <text
                        x="50%"
                        y="50%"
                        textAnchor="middle"
                        dominantBaseline="middle"
                        style={{
                          fontSize: 12,
                          fontWeight: 600,
                          fill: "#000",
                        }}
                      >
                        {chartData.paymentStatusData
                          .reduce((s, d) => s + (d.value || 0), 0)
                          .toLocaleString("en-IN")}
                      </text>
                    )}

                    <Tooltip
                      formatter={(value, name) => [
                        `₹${Number(value).toLocaleString("en-IN")}`,
                        name,
                      ]}
                    />

                    {/* desktop legend on right */}
                    {!isSmallScreen && (
                      <Legend
                        verticalAlign="middle"
                        align="right"
                        layout="vertical"
                        formatter={(value, entry) => {
                          const v = entry.payload.value || 0;  
                          return ( value +" (₹" +v.toLocaleString("en-IN") +")");
                        }}
                      />
                    )}
                  </PieChart>
                </ResponsiveContainer>
              </Box>

              {/* mobile legend under chart */}
              {isSmallScreen && (
                <MobileLegend
                  data={chartData.paymentStatusData}
                  colors={COLORS}
                />
              )}
            </Paper>
          </Grid>

          {/* ====================== Payment Type ====================== */}
          <Grid item xs={12} md={4}>
            <Paper
              elevation={2}
              sx={{
                p: { xs: 2, sm: 2.5 },
                borderRadius: 3,
                background: "white",
                boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
                textAlign: { xs: "center", md: "left" },
                display: "flex",
                flexDirection: "column",
                alignItems: { xs: "center", md: "stretch" },
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  mb: 2,
                  color: "black",
                  fontSize: { xs: "1rem", md: "1.1rem" },
                }}
              >
                Payment Type
              </Typography>

              <Box
                sx={{
                  width: "100%",
                  height: { xs: 200, sm: 230, md: 250 },
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={chartData.paymentTypeData}
                      dataKey="value"
                      nameKey="label"
                      cx={isSmallScreen ? "50%" : "40%"}
                      cy="50%"
                      outerRadius={isSmallScreen ? 70 : 90}
                      innerRadius={isSmallScreen ? 40 : 50}
                      labelLine={false}
                      label={false}
                      isAnimationActive={false}
                    >
                      {chartData.paymentTypeData.map((entry, index) => (
                        <Cell
                          key={`pt-${index}`}
                          fill={COLORS[index % COLORS.length]}
                        />
                      ))}
                    </Pie>

                    {isSmallScreen && (
                      <text
                        x="50%"
                        y="50%"
                        textAnchor="middle"
                        dominantBaseline="middle"
                        style={{
                          fontSize: 12,
                          fontWeight: 600,
                          fill: "#000",
                        }}
                      >
                        {chartData.paymentTypeData
                          .reduce((s, d) => s + (d.value || 0), 0)
                          .toLocaleString("en-IN")}
                      </text>
                    )}

                    <Tooltip />

                    {!isSmallScreen && (
                      <Legend
                        verticalAlign="middle"
                        align="right"
                        layout="vertical"
                        formatter={(value, entry) => {
                          const v = entry.payload.value || 0;
                          return (value +" (₹" +v.toLocaleString("en-IN") +")");
                        }}
                      />
                    )}
                  </PieChart>
                </ResponsiveContainer>
              </Box>

              {isSmallScreen && (
                <MobileLegend
                  data={chartData.paymentTypeData}
                  colors={COLORS}
                />
              )}
            </Paper>
          </Grid>

          {/* ====================== Revenue Over Time ====================== */}
          <Grid item xs={12} md={4}>
            <Paper
              elevation={2}
              sx={{
                p: { xs: 2, sm: 2.5 },
                borderRadius: 3,
                background: "white",
                boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
                textAlign: { xs: "center", md: "left" },
                display: "flex",
                flexDirection: "column",
                alignItems: { xs: "center", md: "stretch" },
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  mb: 2,
                  color: "black",
                  fontSize: { xs: "1rem", md: "1.1rem" },
                }}
              >
                Revenue Over Time
              </Typography>

              <Box
                sx={{ width: "100%", height: { xs: 220, sm: 240, md: 250 } }}
              >
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart
                    data={chartData.revenueOverTimeData}
                    margin={{
                      top: 12,
                      right: isSmallScreen ? 8 : 16,
                      bottom: 12,
                      left: isSmallScreen ? 0 : 8,
                    }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="date"
                      padding={{ left: 20, right: 20 }}
                      tickMargin={8}
                      tick={{ fontSize: isSmallScreen ? 10 : 12 }}
                      tickFormatter={(d) => {
                        const parts = String(d).split("-");
                        if (parts.length >= 2) {
                          const [y, m] = parts;
                          const monthNames = [
                            "Jan",
                            "Feb",
                            "Mar",
                            "Apr",
                            "May",
                            "Jun",
                            "Jul",
                            "Aug",
                            "Sep",
                            "Oct",
                            "Nov",
                            "Dec",
                          ];
                          const mm =
                            Math.max(1, Math.min(12, parseInt(m, 10))) - 1;
                          return `${monthNames[mm]} ${y}`;
                        }
                        return d;
                      }}
                    />
                    <YAxis
                      domain={[0, "auto"]}
                      allowDecimals={false}
                      tickMargin={8}
                      tick={{ fontSize: isSmallScreen ? 10 : 12 }}
                      tickFormatter={(v) => v.toLocaleString("en-IN")}
                    />
                    <Tooltip
                      formatter={(value) =>
                        `₹${Number(value).toLocaleString("en-IN")}`
                      }
                      labelFormatter={(label) => `Month: ${label}`}
                    />
                    <Line
                      type="monotone"
                      dataKey="amount"
                      stroke="#1976d2"
                      strokeWidth={2}
                      dot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </Box>
            </Paper>
          </Grid>

          {/* ====================== Revenue by Professional ====================== */}
          <Grid item xs={12}>
            <Paper
              elevation={2}
              sx={{
                p: { xs: 2, sm: 3 },
                borderRadius: 3,
                background: "white",
                boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  mb: { xs: 2, md: 3 },
                  color: "black",
                  fontSize: { xs: "1rem", md: "1.1rem" },
                  textAlign: { xs: "center", md: "left" },
                }}
              >
                Revenue by Professional
              </Typography>

              <Grid
                container
                spacing={3}
                alignItems="stretch"
                justifyContent="center"
              >
                {/* ----------- Card 1: Assigned vs Other Source ----------- */}
                <Grid item xs={12} md={6} sx={{ display: "flex" }}>
                  <Box
                    sx={{
                      flexGrow: 1,
                      borderRadius: 2,
                      border: "1px solid rgba(0,0,0,0.06)",
                      boxShadow: "0 2px 10px rgba(0,0,0,0.04)",
                      p: { xs: 2, sm: 2.5 },
                      textAlign: { xs: "center", md: "left" },
                      display: "flex",
                      flexDirection: "column",
                      alignItems: { xs: "center", md: "stretch" },
                    }}
                  >
                    {/* Card header */}
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: 600,
                        mb: { xs: 1.5, md: 2 },
                        fontSize: { xs: ".95rem", md: "1rem" },
                        color: "text.primary",
                        textAlign: { xs: "center", md: "left" },
                      }}
                    >
                      Assigned to PRO vs Other Source
                    </Typography>

                    {/* Chart */}
                    <Box
                      sx={{
                        width: "100%",
                        flexGrow: 1,
                        minHeight: { xs: 220, md: 260 },
                      }}
                    >
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart
                          margin={{
                            top: 8,
                            right: isSmallScreen ? 8 : 24,
                            bottom: 8,
                            left: isSmallScreen ? 8 : 24,
                          }}
                        >
                          {(() => {
                            // build data
                            const total = chartData.paymentStatusData.reduce(
                              (s, i) => s + (i.value || 0),
                              0
                            );
                            const assigned = chartData.totalAssigned || 0;
                            const other = Math.max(0, total - assigned);

                            const assignedData = [
                              { label: "Assigned to PRO", value: assigned },
                              { label: "Other Source", value: other },
                            ];

                            // donut
                            return (
                              <>
                                <Pie
                                  data={assignedData}
                                  dataKey="value"
                                  nameKey="label"
                                  cx={isSmallScreen ? "50%" : "40%"}
                                  cy="50%"
                                  outerRadius={isSmallScreen ? 70 : 85}
                                  innerRadius={isSmallScreen ? 40 : 50}
                                  startAngle={90}
                                  endAngle={-270}
                                  labelLine={false}
                                  label={false}
                                  isAnimationActive={false}
                                >
                                  {assignedData.map((entry, index) => (
                                    <Cell
                                      key={`assigned-${index}`}
                                      fill={COLORS[index % COLORS.length]}
                                    />
                                  ))}
                                </Pie>

                                {/* total in the center for mobile */}
                                {isSmallScreen && (
                                  <text
                                    x="50%"
                                    y="50%"
                                    textAnchor="middle"
                                    dominantBaseline="middle"
                                    style={{
                                      fontSize: 13,
                                      fontWeight: 600,
                                      fill: "#000",
                                    }}
                                  >
                                    ₹{assigned.toLocaleString("en-IN")}
                                  </text>
                                )}

                                <Tooltip
                                  formatter={(value) =>
                                    `₹${Number(value).toLocaleString("en-IN")}`
                                  }
                                />

                                {/* Desktop legend on right */}
                                {!isSmallScreen && (
                                  <Legend
                                    verticalAlign="middle"
                                    align="right"
                                    layout="vertical"
                                    formatter={(value, entry) => {
                                      const v = entry.payload.value || 0;
                                      return (value +" (₹" +v.toLocaleString("en-IN") +")");
                                    }}
                                  />
                                )}
                              </>
                            );
                          })()}
                        </PieChart>
                      </ResponsiveContainer>
                    </Box>

                    {/* Mobile legend under chart */}
                    {isSmallScreen &&
                      (() => {
                        const total = chartData.paymentStatusData.reduce(
                          (s, i) => s + (i.value || 0),
                          0
                        );
                        const assigned = chartData.totalAssigned || 0;
                        const other = Math.max(0, total - assigned);
                        const assignedData = [
                          { label: "Assigned to PRO", value: assigned },
                          { label: "Other Source", value: other },
                        ];
                        return (
                          <Box
                            sx={{
                              mt: 1.5,
                              display: "flex",
                              justifyContent: "center",
                              flexWrap: "wrap",
                              rowGap: 1,
                              columnGap: 2,
                            }}
                          >
                            {assignedData.map((item, idx) => (
                              <Box
                                key={idx}
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  fontSize: ".8rem",
                                  fontWeight: 600,
                                  color: "text.primary",
                                }}
                              >
                                <Box
                                  sx={{
                                    width: 10,
                                    height: 10,
                                    borderRadius: "50%",
                                    bgcolor: COLORS[idx % COLORS.length],
                                    mr: 1,
                                  }}
                                />
                                <Typography
                                  component="span"
                                  sx={{
                                    fontSize: ".8rem",
                                    fontWeight: 600,
                                    lineHeight: 1.2,
                                  }}
                                >
                                  {item.label}{" "}
                                  <Typography
                                    component="span"
                                    sx={{
                                      fontWeight: 500,
                                      color: "text.secondary",
                                      fontSize: ".8rem",
                                      ml: 0.5,
                                    }}
                                  >
                                    (₹{(item.value || 0).toLocaleString("en-IN")})
                                  </Typography>
                                </Typography>
                              </Box>
                            ))}
                          </Box>
                        );
                      })()}
                  </Box>
                </Grid>

                {/* ----------- Card 2: PRO Breakdown ----------- */}
                <Grid item xs={12} md={6} sx={{ display: "flex" }}>
                  <Box
                    sx={{
                      flexGrow: 1,
                      borderRadius: 2,
                      border: "1px solid rgba(0,0,0,0.06)",
                      boxShadow: "0 2px 10px rgba(0,0,0,0.04)",
                      p: { xs: 2, sm: 2.5 },
                      textAlign: { xs: "center", md: "left" },
                      display: "flex",
                      flexDirection: "column",
                      alignItems: { xs: "center", md: "stretch" },
                    }}
                  >
                    {/* Header row: title + PRO select */}
                    <Box
                      sx={{
                        width: "100%",
                        display: "flex",
                        flexDirection: { xs: "column", sm: "row" },
                        alignItems: { xs: "flex-start", sm: "center" },
                        justifyContent: { xs: "center", sm: "space-between" },
                        gap: 1.5,
                        mb: { xs: 1.5, md: 2 },
                      }}
                    >
                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontWeight: 600,
                          fontSize: { xs: ".95rem", md: "1rem" },
                          textAlign: { xs: "center", sm: "left" },
                          width: { xs: "100%", sm: "auto" },
                        }}
                      >
                        PRO Breakdown
                      </Typography>

                      <FormControl
                        size="small"
                        sx={{
                          minWidth: { xs: "100%", sm: 180 },
                        }}
                      >
                        <InputLabel id="pro-select-label">
                          Select PRO
                        </InputLabel>
                        <Select
                          labelId="pro-select-label"
                          label="Select PRO"
                          value={selectedPro || ""}
                          onChange={(e) => setSelectedPro(e.target.value)}
                        >
                          <MenuItem value="">-- Select PRO --</MenuItem>
                          {chartData.proRevenueData.map((p) => (
                            <MenuItem key={p.proName} value={p.proName}>
                              {p.proName}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>

                    {/* Summary line */}
                    <Box sx={{ minHeight: 28, mb: { xs: 1, md: 2 } }}>
                      {selectedPro ? (
                        (() => {
                          const proName = selectedPro;
                          const proPayments = payments.filter(
                            (p) => (p.proName || "") === proName
                          );
                          const collected = proPayments.reduce(
                            (s, p) =>
                              s +
                              (p.TASP_IsPaid ? Number(p.TASP_Amount) || 0 : 0),
                            0
                          );
                          const pending = proPayments.reduce(
                            (s, p) =>
                              s +
                              (!p.TASP_IsPaid ? Number(p.TASP_Amount) || 0 : 0),
                            0
                          );
                          const total = collected + pending;

                          return (
                            <Typography
                              variant="subtitle2"
                              sx={{
                                textAlign: "center",
                                fontWeight: 600,
                                fontSize: ".9rem",
                              }}
                            >
                              <Typography
                                component="span"
                                sx={{ fontWeight: 700 }}
                              >
                                {proName}
                              </Typography>{" "}
                              — Total: ₹{total.toLocaleString("en-IN")}
                            </Typography>
                          );
                        })()
                      ) : (
                        <Typography
                          variant="body2"
                          sx={{
                            color: "text.secondary",
                            textAlign: "center",
                          }}
                        >
                          Select a PRO to view collected vs pending amounts.
                        </Typography>
                      )}
                    </Box>

                    {/* Donut + (afterwards) legend */}
                    {selectedPro ? (
                      (() => {
                        // build proData once
                        const proName = selectedPro;
                        const proPayments = payments.filter(
                          (p) => (p.proName || "") === proName
                        );
                        const collected = proPayments.reduce(
                          (s, p) =>
                            s +
                            (p.TASP_IsPaid ? Number(p.TASP_Amount) || 0 : 0),
                          0
                        );
                        const pending = proPayments.reduce(
                          (s, p) =>
                            s +
                            (!p.TASP_IsPaid ? Number(p.TASP_Amount) || 0 : 0),
                          0
                        );
                        const total = collected + pending;

                        const proData = [
                          { label: "Collected", value: collected },
                          { label: "Pending", value: pending },
                        ];

                        return (
                          <>
                            {/* chart itself */}
                            <Box
                              sx={{
                                width: "100%",
                                flexGrow: 1,
                                minHeight: { xs: 220, md: 260 },
                              }}
                            >
                              <ResponsiveContainer width="100%" height="100%">
                                <PieChart
                                  margin={{
                                    top: 8,
                                    right: isSmallScreen ? 8 : 24,
                                    bottom: 8,
                                    left: isSmallScreen ? 8 : 24,
                                  }}
                                >
                                  <Pie
                                    data={proData}
                                    dataKey="value"
                                    nameKey="label"
                                    cx={isSmallScreen ? "50%" : "40%"}
                                    cy="50%"
                                    outerRadius={isSmallScreen ? 70 : 85}
                                    innerRadius={isSmallScreen ? 40 : 50}
                                    startAngle={90}
                                    endAngle={-270}
                                    labelLine={false}
                                    label={false}
                                    isAnimationActive={false}
                                  >
                                    {proData.map((entry, index) => (
                                      <Cell
                                        key={`pro-${index}`}
                                        fill={COLORS[index % COLORS.length]}
                                      />
                                    ))}
                                  </Pie>

                                  {/* total in center on mobile */}
                                  {isSmallScreen && (
                                    <text
                                      x="50%"
                                      y="50%"
                                      textAnchor="middle"
                                      dominantBaseline="middle"
                                      style={{
                                        fontSize: 13,
                                        fontWeight: 600,
                                        fill: "#000",
                                      }}
                                    >
                                      ₹{total.toLocaleString("en-IN")}
                                    </text>
                                  )}

                                  <Tooltip
                                    formatter={(value) =>
                                      `₹${Number(value).toLocaleString(
                                        "en-IN"
                                      )}`
                                    }
                                  />

                                  {/* desktop legend over on the right of the donut */}
                                  {!isSmallScreen && (
                                    <Legend
                                      verticalAlign="middle"
                                      align="right"
                                      layout="vertical"
                                      formatter={(value, entry) => {
                                        const v = entry.payload.value || 0;
                                        return (value +" (₹" +v.toLocaleString("en-IN") +")");
                                      }}
                                    />
                                  )}
                                </PieChart>
                              </ResponsiveContainer>
                            </Box>

                            {/* mobile legend BELOW donut */}
                            {isSmallScreen && (
                              <Box
                                sx={{
                                  mt: 1.5,
                                  display: "flex",
                                  justifyContent: "center",
                                  flexWrap: "wrap",
                                  rowGap: 1,
                                  columnGap: 2,
                                }}
                              >
                                {proData.map((item, idx) => (
                                  <Box
                                    key={idx}
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      fontSize: ".8rem",
                                      fontWeight: 600,
                                      color: "text.primary",
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        width: 10,
                                        height: 10,
                                        borderRadius: "50%",
                                        bgcolor: COLORS[idx % COLORS.length],
                                        mr: 1,
                                      }}
                                    />
                                    <Typography
                                      component="span"
                                      sx={{
                                        fontSize: ".8rem",
                                        fontWeight: 600,
                                        lineHeight: 1.2,
                                      }}
                                    >
                                      {item.label}{" "}
                                      <Typography
                                        component="span"
                                        sx={{
                                          fontWeight: 500,
                                          color: "text.secondary",
                                          fontSize: ".8rem",
                                          ml: 0.5,
                                        }}
                                      >
                                        (₹{(item.value || 0).toLocaleString("en-IN")})
                                      </Typography>
                                    </Typography>
                                  </Box>
                                ))}
                              </Box>
                            )}
                          </>
                        );
                      })()
                    ) : (
                      // no PRO selected
                      <Box
                        sx={{
                          width: "100%",
                          flexGrow: 1,
                          minHeight: { xs: 220, md: 260 },
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "text.disabled",
                          fontSize: ".9rem",
                        }}
                      >
                        No PRO selected.
                      </Box>
                    )}
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* ====================== Revenue by Service Type ====================== */}
          <Grid item xs={12}>
            <Paper
              elevation={2}
              sx={{
                p: { xs: 2, sm: 2.5 },
                borderRadius: 3,
                background: "white",
                boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
                textAlign: { xs: "center", md: "left" },
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  mb: 2,
                  color: "black",
                  fontSize: { xs: "1rem", md: "1.1rem" },
                  textAlign: { xs: "center", md: "left" },
                }}
              >
                Revenue by Service Type
              </Typography>

              <Box
                sx={{
                  width: "100%",
                  height: serviceChartHeight,
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={chartData.serviceRevenueData}
                    layout="vertical"
                    margin={
                      isSmallScreen
                        ? { top: 20, bottom: 20, left: 0, right: 16 }
                        : { top: 20, bottom: 40, left: 20, right: 20 }
                    }
                  >
                    <CartesianGrid
                      strokeDasharray="3 3"
                      vertical={false}
                      stroke="#d9d9d9"
                    />

                    <XAxis
                      type="number"
                      tick={{ fontSize: isSmallScreen ? 10 : 12 }}
                      tickFormatter={(v) =>
                        isSmallScreen
                          ? v >= 1000
                            ? (v / 1000).toFixed(1) + "k"
                            : v
                          : v.toLocaleString("en-IN")
                      }
                    />

                    {isSmallScreen ? (
                      <YAxis
                        type="category"
                        dataKey="serviceName"
                        width={0}
                        tick={false}
                        axisLine={false}
                        tickLine={false}
                        reversed={true}
                      />
                    ) : (
                      <YAxis
                        type="category"
                        dataKey="serviceName"
                        width={Math.min(
                          300,
                          Math.max(
                            120,
                            chartData.serviceRevenueData.reduce(
                              (m, d) =>
                                Math.max(m, (d.serviceName || "").length),
                              0
                            ) * 8
                          )
                        )}
                        tick={{ fontSize: 12, fontWeight: 600 }}
                        axisLine={false}
                        tickLine={false}
                        reversed={true}
                      />
                    )}

                    <Tooltip
                      formatter={(value) =>
                        `₹${Number(value).toLocaleString("en-IN")}`
                      }
                      labelFormatter={(label) => `Service: ${label}`}
                      cursor={{ fill: "rgba(0,0,0,0.04)" }}
                    />

                    {isSmallScreen ? (
                      <Bar
                        dataKey="amount"
                        fill="#29b6f6"
                        radius={[0, 6, 6, 0]}
                        barSize={28}
                      >
                        <LabelList
                          dataKey="serviceName"
                          position="insideLeft"
                          style={{
                            fill: "#000",
                            fontWeight: 600,
                            fontSize: 11,
                          }}
                        />
                        <LabelList
                          dataKey="amount"
                          position="right"
                          formatter={(v) =>
                            `₹${Number(v).toLocaleString("en-IN")}`
                          }
                          style={{
                            fill: "#000",
                            fontWeight: 600,
                            fontSize: 10,
                          }}
                        />
                      </Bar>
                    ) : (
                      <Bar
                        dataKey="amount"
                        fill="#29b6f6"
                        radius={[0, 6, 6, 0]}
                        barSize={18}
                      />
                    )}
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      )}
    </Box>
  );
}

export default PatPayReportGraphs;
